import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { formatGBP, formatUKDate, todayISO, addDaysISO, startOfWeekISO } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { ChevronLeft, ChevronRight } from "lucide-react";

const VIEWS = ["Day", "Week", "Month", "List"];

export default function CalendarPage() {
  const [view, setView] = useState("Week");
  const [anchor, setAnchor] = useState(todayISO());
  const [jobs, setJobs] = useState([]);

  const range = useMemo(() => {
    if (view === "Day") return { from: anchor, to: anchor };
    if (view === "Week") {
      const from = startOfWeekISO(anchor);
      return { from, to: addDaysISO(from, 6) };
    }
    if (view === "Month") {
      const d = new Date(anchor + "T00:00:00");
      const from = new Date(d.getFullYear(), d.getMonth(), 1);
      const to = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
    }
    // List = next 60 days
    return { from: anchor, to: addDaysISO(anchor, 60) };
  }, [view, anchor]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/jobs", { params: { date_from: range.from, date_to: range.to } });
      setJobs(data);
    })();
  }, [range.from, range.to]);

  const byDate = useMemo(() => {
    const m = {};
    for (const j of jobs) { (m[j.scheduled_date] = m[j.scheduled_date] || []).push(j); }
    return m;
  }, [jobs]);

  const shift = (delta) => {
    if (view === "Day") setAnchor(addDaysISO(anchor, delta));
    else if (view === "Week") setAnchor(addDaysISO(anchor, delta * 7));
    else if (view === "Month") {
      const d = new Date(anchor + "T00:00:00");
      d.setMonth(d.getMonth() + delta);
      setAnchor(d.toISOString().slice(0, 10));
    } else setAnchor(addDaysISO(anchor, delta * 30));
  };

  const title = () => {
    const d = new Date(anchor + "T00:00:00");
    if (view === "Month") return d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
    return `${formatUKDate(range.from)} — ${formatUKDate(range.to)}`;
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="calendar-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl tracking-tight font-bold">Calendar</h1>
        <div className="inline-flex rounded-md border border-border bg-card p-1 text-sm">
          {VIEWS.map((v) => (
            <button key={v} onClick={() => setView(v)} className={`px-3 py-1.5 rounded ${view === v ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid={`calendar-view-${v.toLowerCase()}`}>{v}</button>
          ))}
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button className="h-8 w-8 grid place-items-center rounded-md border border-border hover:bg-muted" onClick={() => shift(-1)} data-testid="calendar-prev"><ChevronLeft className="h-4 w-4" /></button>
        <button className="text-sm px-3 h-8 rounded-md border border-border hover:bg-muted" onClick={() => setAnchor(todayISO())} data-testid="calendar-today">Today</button>
        <button className="h-8 w-8 grid place-items-center rounded-md border border-border hover:bg-muted" onClick={() => shift(1)} data-testid="calendar-next"><ChevronRight className="h-4 w-4" /></button>
        <div className="text-sm text-muted-foreground ml-2">{title()}</div>
      </div>

      <div className="mt-6">
        {view === "Day" && <DayView date={anchor} jobs={byDate[anchor] || []} />}
        {view === "Week" && <WeekView from={range.from} byDate={byDate} />}
        {view === "Month" && <MonthView anchor={anchor} byDate={byDate} />}
        {view === "List" && <ListView jobs={jobs} />}
      </div>
    </div>
  );
}

function DayView({ date, jobs }) {
  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="day-view">
      <h2 className="font-display text-xl font-bold">{formatUKDate(date)}</h2>
      {jobs.length === 0 ? <div className="mt-4 text-sm text-muted-foreground">No jobs.</div> : (
        <ul className="mt-4 divide-y divide-border">
          {jobs.map((j) => (
            <li key={j.id} className="py-3 flex items-center justify-between">
              <div>
                <div className="font-medium">{j.customer_name}</div>
                <div className="text-xs text-muted-foreground">{j.customer_address} · {j.customer_postcode}</div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-sm font-semibold">{formatGBP(j.price)}</div>
                <StatusBadge status={j.status} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function WeekView({ from, byDate }) {
  const days = Array.from({ length: 7 }, (_, i) => addDaysISO(from, i));
  return (
    <div className="grid grid-cols-1 md:grid-cols-7 gap-3" data-testid="week-view">
      {days.map((d) => {
        const list = byDate[d] || [];
        const isToday = d === todayISO();
        return (
          <div key={d} className={`rounded-lg border bg-card p-3 min-h-[160px] ${isToday ? "border-primary" : "border-border"}`}>
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">
                {new Date(d + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short" })}
              </div>
              <div className={`text-sm font-semibold ${isToday ? "text-primary" : ""}`}>{new Date(d + "T00:00:00").getDate()}</div>
            </div>
            <div className="mt-2 space-y-1">
              {list.slice(0, 5).map((j) => (
                <div key={j.id} className="rounded px-2 py-1 text-xs truncate" style={{ background: j.status === "completed" ? "#d1fae5" : j.status === "no_access" ? "#fecdd3" : "#dbeafe" }}>
                  {j.customer_name} · {formatGBP(j.price)}
                </div>
              ))}
              {list.length > 5 && <div className="text-xs text-muted-foreground">+{list.length - 5} more</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MonthView({ anchor, byDate }) {
  const d = new Date(anchor + "T00:00:00");
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const startDay = (first.getDay() + 6) % 7; // mon=0
  const daysInMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let i = 1; i <= daysInMonth; i++) cells.push(new Date(d.getFullYear(), d.getMonth(), i).toISOString().slice(0, 10));
  return (
    <div data-testid="month-view">
      <div className="grid grid-cols-7 text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="px-2 py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => {
          if (!c) return <div key={i} className="h-24" />;
          const list = byDate[c] || [];
          const isToday = c === todayISO();
          return (
            <div key={c} className={`h-24 rounded-md border p-1.5 bg-card text-xs ${isToday ? "border-primary ring-1 ring-primary/30" : "border-border"}`}>
              <div className={`text-xs font-semibold ${isToday ? "text-primary" : ""}`}>{new Date(c + "T00:00:00").getDate()}</div>
              <div className="mt-1 space-y-0.5">
                {list.slice(0, 2).map((j) => (
                  <div key={j.id} className="truncate rounded px-1" style={{ background: j.status === "completed" ? "#d1fae5" : j.status === "no_access" ? "#fecdd3" : "#dbeafe" }}>
                    {j.customer_name}
                  </div>
                ))}
                {list.length > 2 && <div className="text-muted-foreground">+{list.length - 2}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ListView({ jobs }) {
  const sorted = [...jobs].sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date));
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="list-view">
      <table className="w-full text-sm">
        <thead className="bg-accent text-left text-xs uppercase tracking-[0.14em] font-bold text-muted-foreground">
          <tr>
            <th className="px-4 py-3">Date</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Postcode</th>
            <th className="px-4 py-3">Price</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Payment</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {sorted.length === 0 ? (
            <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No jobs in range.</td></tr>
          ) : sorted.map((j) => (
            <tr key={j.id}>
              <td className="px-4 py-3 whitespace-nowrap">{formatUKDate(j.scheduled_date)}</td>
              <td className="px-4 py-3"><div className="font-medium">{j.customer_name}</div><div className="text-xs text-muted-foreground">{j.customer_address}</div></td>
              <td className="px-4 py-3 font-mono text-xs">{j.customer_postcode}</td>
              <td className="px-4 py-3">{formatGBP(j.price)}</td>
              <td className="px-4 py-3"><StatusBadge status={j.status} /></td>
              <td className="px-4 py-3"><StatusBadge status={j.payment_status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
